import { RRule } from "rrule";
//#region src/components/RRule/utils/wall-clock-time.ts
/**
* Wall-clock helpers for RRULEs that are interpreted in a *server-side*
* timezone (e.g. a caller using the Region timezone). The picked local
* time must land in the RRULE verbatim — no UTC conversion.
*/
function timePickToWallClock(iso) {
	const date = new Date(iso);
	return {
		byhour: date.getHours(),
		byminute: date.getMinutes(),
		bysecond: 0
	};
}
/** Atalaya DateTimePicker ISO value -> wall-clock time of day. */
function timePickToTimeOfDay(iso) {
	const { byhour, byminute } = timePickToWallClock(iso);
	return {
		hour: byhour,
		minute: byminute
	};
}
function wallClockToTimePickValue(byhour, byminute) {
	const date = /* @__PURE__ */ new Date();
	date.setHours(byhour, byminute, 0, 0);
	return date.toISOString();
}
function startTimeOfDayFromRRuleString(rrule) {
	if (!/BYHOUR=/i.test(rrule)) return null;
	try {
		const options = RRule.fromString(rrule).options;
		const hour = Array.isArray(options.byhour) ? options.byhour[0] : options.byhour;
		const minute = Array.isArray(options.byminute) ? options.byminute[0] : options.byminute;
		if (typeof hour !== "number") return null;
		return {
			hour,
			minute: typeof minute === "number" ? minute : 0
		};
	} catch {
		return null;
	}
}
function resolveWallClockHour(hr) {
	return hr;
}
/** Bug-compatible conversion for callers not yet on wall-clock RRULEs — treats
* `hr` as UTC and converts to the viewer's browser-local hour. */
function resolveLegacyUtcHour(hr) {
	const d = /* @__PURE__ */ new Date();
	d.setUTCHours(hr);
	return d.getHours();
}
function formatClockHours(hours24, byminute) {
	const min = `${byminute || 0}`.padStart(2, "0");
	return hours24.map((hour24) => {
		return `${(hour24 + 11) % 12 + 1}:${min}${hour24 > 11 ? "pm" : "am"}`;
	}).join(", ");
}
/** Resolves an RRULE's BYHOUR values per `wallClockTime` and formats them —
* the one call `RRuleText`/`RRuleTag` need for their `hour` display. */
function formatRRuleHours(byhour, byminute, wallClockTime) {
	const resolveHour = wallClockTime ? resolveWallClockHour : resolveLegacyUtcHour;
	return formatClockHours(byhour.map(resolveHour), byminute);
}
/**
* Start/end clock labels for an RRULE that carries a window length. Returns
* `null` when the range would be ambiguous or meaningless — a rule with several
* BYHOUR starts, or a non-positive duration — so callers can fall back to the
* bare start-time display.
*/
function formatRRuleWallClockRange(byhour, byminute, durationMin, wallClockTime) {
	if (byhour?.length !== 1 || !(durationMin > 0)) return null;
	const startHour = (wallClockTime ? resolveWallClockHour : resolveLegacyUtcHour)(byhour[0]);
	const startMin = startHour * 60 + (byminute || 0);
	const endMin = ((startMin + durationMin) % 1440 + 1440) % 1440;
	return {
		end: formatClockHours([Math.floor(endMin / 60)], endMin % 60),
		start: formatClockHours([startHour], byminute),
		wraps: startMin + durationMin >= 1440
	};
}
//#endregion
export { formatClockHours, formatRRuleHours, formatRRuleWallClockRange, resolveLegacyUtcHour, resolveWallClockHour, startTimeOfDayFromRRuleString, timePickToTimeOfDay, timePickToWallClock, wallClockToTimePickValue };

//# sourceMappingURL=wall-clock-time.js.map